import React, { useState, useEffect, useRef, useCallback } from 'react';

// ── Minimal Markdown renderer ──────────────────────────────────────────────
const renderMarkdown = (text) => {
  if (!text) return '';
  let html = text
    // Code blocks ```lang\n...\n```
    .replace(/```(\w*)\n?([\s\S]*?)```/g, (_, lang, code) =>
      `<pre class="code-block"><code class="lang-${lang || 'text'}">${escapeHtml(code.trim())}</code></pre>`
    )
    // Inline code `...`
    .replace(/`([^`\n]+)`/g, (_, code) =>
      `<code class="inline-code">${escapeHtml(code)}</code>`
    )
    // Bold **text**
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    // Italic *text*
    .replace(/\*(.+?)\*/g, '<em>$1</em>')
    // Unordered list - item
    .replace(/^[ \t]*[-•]\s+(.+)$/gm, '<li>$1</li>')
    // Ordered list 1. item
    .replace(/^[ \t]*\d+\.\s+(.+)$/gm, '<li class="ordered">$1</li>')
    // Wrap consecutive <li> in <ul>/<ol>
    .replace(/(<li(?:\s[^>]*)?>[\s\S]*?<\/li>(?:\s*<li(?:\s[^>]*)?>[\s\S]*?<\/li>)*)/g,
      (m) => m.includes('class="ordered"') ? `<ol>${m}</ol>` : `<ul>${m}</ul>`)
    // Headings ### ## #
    .replace(/^###\s+(.+)$/gm, '<h4>$1</h4>')
    .replace(/^##\s+(.+)$/gm, '<h3>$1</h3>')
    .replace(/^#\s+(.+)$/gm, '<h2>$1</h2>')
    // Horizontal rule ---
    .replace(/^---$/gm, '<hr />')
    // Line breaks (double newline → paragraph break, single → <br>)
    .replace(/\n\n/g, '</p><p>')
    .replace(/\n/g, '<br/>');

  return `<p>${html}</p>`;
};

const escapeHtml = (str) =>
  str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

// ── Message bubble ─────────────────────────────────────────────────────────
const MessageBubble = ({ message, isStreaming }) => {
  const isBot = message.type === 'bot';
  return (
    <div className={`message-row ${message.type}`}>
      {isBot && (
        <div className="avatar bot-av">
          <BotIcon />
        </div>
      )}
      <div className={`bubble ${message.type}`}>
        {isBot ? (
          <div
            className={`bubble-inner markdown ${isStreaming ? 'streaming' : ''}`}
            dangerouslySetInnerHTML={{ __html: renderMarkdown(message.text) }}
          />
        ) : (
          <div className="bubble-inner">{message.text}</div>
        )}
        {isStreaming && <span className="cursor-blink">▍</span>}
      </div>
      {!isBot && (
        <div className="avatar user-av">
          <UserIcon />
        </div>
      )}
    </div>
  );
};

// ── Icons ──────────────────────────────────────────────────────────────────
const BotIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
    <rect x="3" y="8" width="18" height="13" rx="3" />
    <path d="M8 8V6a4 4 0 018 0v2" />
    <circle cx="9" cy="14" r="1.2" fill="currentColor" stroke="none" />
    <circle cx="15" cy="14" r="1.2" fill="currentColor" stroke="none" />
    <path d="M9.5 17.5s.8 1 2.5 1 2.5-1 2.5-1" strokeLinecap="round" />
  </svg>
);

const UserIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
    <circle cx="12" cy="8" r="4" />
    <path d="M4 20c0-4 3.6-7 8-7s8 3 8 7" strokeLinecap="round" />
  </svg>
);

const SendIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor">
    <path d="M3.4 20.4l17.45-7.48a1 1 0 000-1.84L3.4 3.6a.993.993 0 00-1.39.91L2 9.12c0 .5.37.93.87.99L17 12 2.87 13.88c-.5.07-.87.5-.87 1l.01 4.61c0 .71.73 1.2 1.39.91z" />
  </svg>
);

// ── Quick suggestions ──────────────────────────────────────────────────────
const SUGGESTIONS = [
  "Quelles sont les tendances IA en Afrique ?",
  "Comment la data transforme l'Afrique ?",
  "Startups IA africaines prometteuses",
  "Défis de l'IA sur le continent",
];

// ── Main Component ─────────────────────────────────────────────────────────
const FloatingChatbot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      id: 'init',
      type: 'bot',
      text: "Bonjour ! Je suis l'assistant **Aifrica**. Je suis là pour répondre à vos questions sur l'intelligence artificielle et la data en Afrique. Comment puis-je vous aider ?",
    },
  ]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [streamingId, setStreamingId] = useState(null);
  const [showSuggestions, setShowSuggestions] = useState(true);
  const [sessionId] = useState(() => `user_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`);
  const [hasNewMessage, setHasNewMessage] = useState(false);

  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const textareaRef = useRef(null);
  const isAtBottomRef = useRef(true);
  const chatWindowRef = useRef(null);

  // Track scroll position
  const handleScroll = useCallback(() => {
    const el = messagesContainerRef.current;
    if (!el) return;
    isAtBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 60;
  }, []);

  const scrollToBottom = useCallback((force = false) => {
    if (force || isAtBottomRef.current) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Fermer automatiquement le chatbot lors des clics à l'extérieur
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Vérifier si le clic est en dehors de la fenêtre du chatbot
      if (chatWindowRef.current && !chatWindowRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    // Ajouter l'écouteur d'événements uniquement quand le chatbot est ouvert
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    // Nettoyer l'écouteur d'événements
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Auto-resize textarea
  const handleInput = (e) => {
    const ta = e.target;
    ta.style.height = 'auto';
    ta.style.height = Math.min(ta.scrollHeight, 120) + 'px';
    setInputMessage(ta.value);
  };

  // Simulate streaming — splits actual response into chunks and types them
  const streamText = useCallback((msgId, fullText) => {
    return new Promise((resolve) => {
      const words = fullText.split(' ');
      let i = 0;
      // Vary speed for natural feel
      const typeNext = () => {
        if (i >= words.length) {
          setStreamingId(null);
          resolve();
          return;
        }
        // Chunk 1-3 words at a time
        const chunk = words.slice(i, i + Math.ceil(Math.random() * 2 + 1)).join(' ');
        i += chunk.split(' ').length;
        setMessages((prev) =>
          prev.map((m) =>
            m.id === msgId
              ? { ...m, text: m.text + (m.text ? ' ' : '') + chunk }
              : m
          )
        );
        scrollToBottom();
        // Natural cadence: punctuation = longer pause
        const lastChar = chunk.slice(-1);
        const delay = /[.!?]/.test(lastChar) ? 80 + Math.random() * 60
          : /[,;:]/.test(lastChar) ? 40 + Math.random() * 30
          : 18 + Math.random() * 22;
        setTimeout(typeNext, delay);
      };
      typeNext();
    });
  }, [scrollToBottom]);

  const sendMessage = async (text) => {
    const userMessage = (text || inputMessage).trim();
    if (!userMessage || isLoading) return;

    setInputMessage('');
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }
    setIsLoading(true);
    setShowSuggestions(false);
    isAtBottomRef.current = true;

    const userId = `u_${Date.now()}`;
    setMessages((prev) => [...prev, { id: userId, type: 'user', text: userMessage }]);

    const botId = `b_${Date.now()}`;
    setMessages((prev) => [...prev, { id: botId, type: 'bot', text: '' }]);
    setStreamingId(botId);
    scrollToBottom(true);

    try {
      const response = await fetch('https://aifrica-chatbot-backend.onrender.com/chat', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: 'Bearer aifrica_93KslfP2039dkfP29dkfP29',
        },
        body: JSON.stringify({ question: userMessage, session_id: sessionId }),
      });

      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data = await response.json();
      const answer =
        data.answer || "Désolé, je n'ai pas pu traiter votre demande. Veuillez réessayer.";

      await streamText(botId, answer);

      if (!isOpen) setHasNewMessage(true);
    } catch (err) {
      console.error(err);
      await streamText(
        botId,
        "Désolé, une erreur s'est produite. Veuillez vérifier votre connexion et réessayer."
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const openChat = () => {
    setIsOpen(true);
    setHasNewMessage(false);
    setTimeout(() => scrollToBottom(true), 100);
  };

  return (
    <>
      <style>{styles}</style>
      <div className="fc-root">
        {/* ── Toggle button ── */}
        {!isOpen && (
          <button className="fc-fab" onClick={openChat} aria-label="Ouvrir le chat">
            {hasNewMessage && <span className="fc-badge" />}
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" />
            </svg>
          </button>
        )}

        {/* ── Chat window ── */}
        <div ref={chatWindowRef} className={`fc-window ${isOpen ? 'open' : ''}`} role="dialog" aria-label="Aifrica Assistant">
          {/* Header */}
          <div className="fc-header">
            <div className="fc-header-left">
              <div className="fc-header-avatar">
                <BotIcon />
                <span className="fc-online-dot" />
              </div>
              <div>
                <div className="fc-header-name">Assistant Aifrica</div>
                <div className="fc-header-sub">
                  {isLoading ? (
                    <span className="fc-typing-label">
                      <span className="fc-typing-dot" /><span className="fc-typing-dot" /><span className="fc-typing-dot" />
                      en train d'écrire…
                    </span>
                  ) : (
                    'Votre expert en IA & Data'
                  )}
                </div>
              </div>
            </div>
            <button className="fc-close" onClick={() => setIsOpen(false)} aria-label="Fermer">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
                <path d="M18 6L6 18M6 6l12 12" strokeLinecap="round" />
              </svg>
            </button>
          </div>

          {/* Messages */}
          <div
            className="fc-messages"
            ref={messagesContainerRef}
            onScroll={handleScroll}
          >
            {messages.map((msg) => (
              <MessageBubble
                key={msg.id}
                message={msg}
                isStreaming={streamingId === msg.id}
              />
            ))}

            {/* Suggestions */}
            {showSuggestions && !isLoading && (
              <div className="fc-suggestions">
                <p className="fc-suggestions-label">Questions fréquentes</p>
                <div className="fc-suggestions-grid">
                  {SUGGESTIONS.map((s) => (
                    <button
                      key={s}
                      className="fc-suggestion-chip"
                      onClick={() => sendMessage(s)}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="fc-input-area">
            <div className="fc-input-box">
              <textarea
                ref={textareaRef}
                value={inputMessage}
                onChange={handleInput}
                onKeyDown={handleKeyDown}
                placeholder="Envoyer un message…"
                rows={1}
                disabled={isLoading}
                className="fc-textarea"
              />
              <button
                className="fc-send"
                onClick={() => sendMessage()}
                disabled={!inputMessage.trim() || isLoading}
                aria-label="Envoyer"
              >
                <SendIcon />
              </button>
            </div>
            <p className="fc-disclaimer">L'IA peut faire des erreurs. Vérifiez les informations importantes.</p>
          </div>
        </div>
      </div>
    </>
  );
};

// ── Styles ─────────────────────────────────────────────────────────────────
const styles = `
@import url('https://fonts.googleapis.com/css2?family=Sora:wght@300;400;500;600&display=swap');

.fc-root {
  position: fixed;
  bottom: 28px;
  right: 28px;
  z-index: 9999;
  font-family: 'Sora', system-ui, sans-serif;
  --navy: #0f2537;
  --navy-2: #1a3a52;
  --gold: #c8961a;
  --gold-l: #f4c430;
  --gold-pale: #fef9ec;
  --white: #ffffff;
  --bg: #f7f8fa;
  --text: #111827;
  --muted: #6b7280;
  --border: #e4e7eb;
  --bubble-bot: #ffffff;
  --bubble-user-1: #0f2537;
  --bubble-user-2: #1a3a52;
  --radius: 18px;
  --shadow: 0 20px 60px rgba(15,37,55,.18), 0 4px 16px rgba(15,37,55,.1);
}

/* FAB */
.fc-fab {
  width: 58px; height: 58px;
  border-radius: 50%;
  border: none;
  background: var(--navy);
  color: var(--gold-l);
  cursor: pointer;
  display: flex; align-items: center; justify-content: center;
  box-shadow: 0 8px 30px rgba(15,37,55,.4), 0 0 0 2.5px var(--gold) inset;
  transition: transform .25s cubic-bezier(.34,1.56,.64,1), box-shadow .2s;
  position: relative;
}
.fc-fab svg { width: 24px; height: 24px; }
.fc-fab:hover {
  transform: scale(1.08) translateY(-2px);
  box-shadow: 0 14px 40px rgba(15,37,55,.45), 0 0 0 2.5px var(--gold-l) inset;
}
.fc-badge {
  position: absolute; top: 4px; right: 4px;
  width: 12px; height: 12px;
  border-radius: 50%;
  background: #ef4444;
  border: 2px solid white;
  animation: badge-pulse 1.8s infinite;
}
@keyframes badge-pulse {
  0%,100% { box-shadow: 0 0 0 0 rgba(239,68,68,.5); }
  50% { box-shadow: 0 0 0 6px rgba(239,68,68,0); }
}

/* Window */
.fc-window {
  position: absolute;
  bottom: -25px; right: 0;
  width: 380px; height: 580px;
  background: var(--white);
  border-radius: var(--radius);
  box-shadow: var(--shadow);
  border: 1px solid rgba(212,160,23,.12);
  display: flex; flex-direction: column;
  overflow: hidden;
  transform-origin: bottom right;
  transform: scale(.88) translateY(16px);
  opacity: 0;
  pointer-events: none;
  transition: transform .35s cubic-bezier(.34,1.3,.64,1), opacity .22s ease;
}
.fc-window.open {
  transform: scale(1) translateY(0);
  opacity: 1;
  pointer-events: all;
}

/* Header */
.fc-header {
  background: linear-gradient(135deg, var(--navy) 0%, var(--navy-2) 100%);
  padding: 14px 16px;
  display: flex; align-items: center; justify-content: space-between;
  position: relative; flex-shrink: 0;
}
.fc-header::after {
  content:''; position:absolute; bottom:0; left:0; right:0;
  height:2px;
  background: linear-gradient(90deg, var(--gold), var(--gold-l), var(--gold));
}
.fc-header-left { display:flex; align-items:center; gap:11px; }
.fc-header-avatar {
  width:40px; height:40px;
  border-radius:50%;
  background: rgba(212,160,23,.15);
  border:1.5px solid rgba(212,160,23,.4);
  display:flex; align-items:center; justify-content:center;
  color: var(--gold-l);
  position:relative; flex-shrink:0;
}
.fc-header-avatar svg { width:22px; height:22px; }
.fc-online-dot {
  position:absolute; bottom:1px; right:1px;
  width:10px; height:10px;
  border-radius:50%;
  background:#22c55e;
  border:2px solid var(--navy);
}
.fc-header-name {
  font-size:14px; font-weight:600;
  color:white; letter-spacing:.2px;
}
.fc-header-sub {
  font-size:11px; color: rgba(244,196,48,.8);
  margin-top:1px; height:14px;
  display:flex; align-items:center; gap:4px;
}
.fc-typing-label {
  display:flex; align-items:center; gap:4px;
  font-style:italic;
}
.fc-typing-dot {
  width:5px; height:5px;
  border-radius:50%;
  background:var(--gold-l);
  display:inline-block;
  animation: typing-bounce .9s infinite ease-in-out;
}
.fc-typing-dot:nth-child(2) { animation-delay:.15s; }
.fc-typing-dot:nth-child(3) { animation-delay:.3s; }
@keyframes typing-bounce {
  0%,80%,100% { transform:translateY(0); opacity:.4; }
  40% { transform:translateY(-4px); opacity:1; }
}
.fc-close {
  background: rgba(255,255,255,.08);
  border:1px solid rgba(255,255,255,.12);
  border-radius:8px;
  color:rgba(255,255,255,.6);
  width:30px; height:30px;
  display:flex; align-items:center; justify-content:center;
  cursor:pointer;
  transition: background .15s, color .15s;
}
.fc-close svg { width:16px; height:16px; }
.fc-close:hover { background:rgba(255,255,255,.16); color:white; }

/* Messages area */
.fc-messages {
  flex:1; overflow-y:auto;
  padding:18px 14px 10px;
  background:var(--bg);
  display:flex; flex-direction:column;
  gap:4px;
  scroll-behavior:smooth;
}
.fc-messages::-webkit-scrollbar { width:4px; }
.fc-messages::-webkit-scrollbar-track { background:transparent; }
.fc-messages::-webkit-scrollbar-thumb {
  background:rgba(212,160,23,.25); border-radius:4px;
}

/* Message rows */
.message-row {
  display:flex; align-items:flex-end; gap:8px;
  animation: msg-in .26s cubic-bezier(.34,1.2,.64,1) both;
}
@keyframes msg-in {
  from { opacity:0; transform:translateY(8px) scale(.97); }
  to   { opacity:1; transform:translateY(0) scale(1); }
}
.message-row.user { flex-direction:row-reverse; justify-content:flex-start; }
.message-row.bot  { justify-content:flex-start; }

.avatar {
  width:30px; height:30px;
  border-radius:50%;
  display:flex; align-items:center; justify-content:center;
  flex-shrink:0; margin-bottom:2px;
}
.avatar svg { width:16px; height:16px; }
.bot-av {
  background:linear-gradient(135deg, var(--navy), var(--navy-2));
  border:1.5px solid rgba(212,160,23,.3);
  color:var(--gold-l);
}
.user-av {
  background:linear-gradient(135deg, var(--gold), var(--gold-l));
  color:var(--navy);
}

/* Bubbles */
.bubble {
  max-width:calc(100% - 50px);
  position:relative;
}
.bubble.bot .bubble-inner {
  background:var(--bubble-bot);
  color:var(--text);
  border-radius:16px 16px 16px 4px;
  box-shadow:0 1px 8px rgba(15,37,55,.08);
  border:1px solid var(--border);
  padding:10px 14px;
  font-size:13.5px; line-height:1.6;
}
.bubble.user .bubble-inner {
  background:linear-gradient(135deg, var(--bubble-user-1), var(--bubble-user-2));
  color:white;
  border-radius:16px 16px 4px 16px;
  box-shadow:0 4px 16px rgba(15,37,55,.25);
  padding:10px 14px;
  font-size:13.5px; line-height:1.6;
}

/* Cursor blink */
.cursor-blink {
  display:inline-block;
  color:var(--gold);
  animation:cursor-blink .7s step-end infinite;
  font-size:14px; margin-left:1px;
}
@keyframes cursor-blink {
  0%,100% { opacity:1; }
  50% { opacity:0; }
}

/* Markdown inside bot bubble */
.markdown p { margin:0 0 6px; }
.markdown p:last-child { margin-bottom:0; }
.markdown h2,.markdown h3,.markdown h4 {
  font-weight:600; margin:10px 0 4px; color:var(--navy);
}
.markdown h2 { font-size:15px; }
.markdown h3 { font-size:14px; }
.markdown h4 { font-size:13.5px; }
.markdown ul, .markdown ol {
  margin:6px 0; padding-left:18px;
}
.markdown li { margin:2px 0; }
.markdown strong { font-weight:600; }
.markdown em { font-style:italic; color:var(--muted); }
.markdown hr { border:none; border-top:1px solid var(--border); margin:10px 0; }
.markdown code.inline-code {
  background:var(--bg);
  border:1px solid var(--border);
  border-radius:4px;
  padding:1px 5px;
  font-family:monospace;
  font-size:12.5px;
  color:var(--navy-2);
}
.markdown pre.code-block {
  background:#0f172a;
  border-radius:10px;
  padding:12px 14px;
  margin:8px 0;
  overflow-x:auto;
}
.markdown pre.code-block code {
  font-family:'Courier New', monospace;
  font-size:12px; line-height:1.55;
  color:#e2e8f0;
}

/* Suggestions */
.fc-suggestions { margin-top:6px; }
.fc-suggestions-label {
  font-size:11px; color:var(--muted);
  margin:0 0 8px 4px; font-weight:500;
  text-transform:uppercase; letter-spacing:.5px;
}
.fc-suggestions-grid {
  display:flex; flex-direction:column; gap:6px;
}
.fc-suggestion-chip {
  background:white;
  border:1px solid var(--border);
  border-radius:10px;
  padding:8px 12px;
  font-size:12.5px;
  color:var(--text);
  cursor:pointer;
  text-align:left;
  transition:border-color .15s, background .15s, transform .15s;
  font-family:inherit;
}
.fc-suggestion-chip:hover {
  border-color:var(--gold);
  background:var(--gold-pale);
  transform:translateX(2px);
}

/* Input area */
.fc-input-area {
  background:white;
  border-top:1px solid var(--border);
  padding:12px 14px 10px;
  flex-shrink:0;
}
.fc-input-box {
  display:flex; align-items:flex-end; gap:8px;
  background:var(--bg);
  border:1.5px solid var(--border);
  border-radius:14px;
  padding:8px 8px 8px 14px;
  transition:border-color .2s, box-shadow .2s;
}
.fc-input-box:focus-within {
  border-color:var(--gold);
  box-shadow:0 0 0 3px rgba(200,150,26,.1);
}
.fc-textarea {
  flex:1; border:none; background:transparent;
  font-family:inherit; font-size:13.5px;
  color:var(--text); resize:none; outline:none;
  max-height:120px; line-height:1.5;
  padding:3px 0;
  overflow-y:auto;
}
.fc-textarea::placeholder { color:var(--muted); }
.fc-textarea:disabled { opacity:.5; cursor:not-allowed; }
.fc-send {
  width:36px; height:36px;
  background:var(--navy);
  border:none; border-radius:10px;
  color:var(--gold-l);
  cursor:pointer;
  display:flex; align-items:center; justify-content:center;
  flex-shrink:0;
  transition:transform .18s, box-shadow .18s, opacity .15s, background .15s;
}
.fc-send svg { width:17px; height:17px; }
.fc-send:hover:not(:disabled) {
  transform:scale(1.06);
  background:var(--navy-2);
  box-shadow:0 4px 14px rgba(15,37,55,.3);
}
.fc-send:disabled { opacity:.3; cursor:not-allowed; }
.fc-disclaimer {
  font-size:10px; color:var(--muted);
  text-align:center; margin:6px 0 0;
}

/* Responsive */
@media(max-width:480px) {
  .fc-root { bottom:16px; right:16px; }
  .fc-window { width:calc(100vw - 32px); height:75vh; bottom:74px; border-radius:16px; }
}
`;

export default FloatingChatbot;